import { combineReducers, configureStore } from '@reduxjs/toolkit';
import { shallowEqual, type TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';
import amplitudeAnalyticsSettings from '@alltrails/amplitude/slices/amplitudeAnalyticsSettings';
import context from '@alltrails/context/slices/contextSlice';
import currencyCodeReducer from '@alltrails/context/slices/currencyCodeSlice';
import plans from '@alltrails/context/slices/plansSlice';
import type RequiredPreloadedState from '@alltrails/core/types/RequiredPreloadedState';
import { experimentsReducer as experiments } from '@alltrails/experiments';
import features from '@alltrails/features/slices/features';
import installBanner, { InstallBannerState } from '@alltrails/install-prompt/slices/installBanner';
import lists, { type ListsState } from '@alltrails/lists/slices/lists';
import { mapReducer, type MapState } from '@alltrails/maps/redux/reducer';
import { baseApi } from '@alltrails/redux-helpers';
import algoliaConfigs from '@alltrails/search/slices/algoliaConfigs';
import userConnectionLists from '@alltrails/social-connections/slices/userConnectionLists';
import userConnections from '@alltrails/social-connections/slices/userConnections';
import { AlgoliaConfigs } from '@alltrails/types/algoliaConfigs';
import type Context from '@alltrails/types/Context';
import type CountryCode from '@alltrails/types/CountryCode';
import { listenerMiddleware } from './listenerMiddleware';

export type PreloadedState = RequiredPreloadedState & {
  algoliaConfigs: AlgoliaConfigs;
  installBanner?: InstallBannerState;
  lists: ListsState;
  map: MapState;
};

export const reducer = combineReducers({
  [baseApi.reducerPath]: baseApi.reducer,
  algoliaConfigs,
  amplitudeAnalyticsSettings,
  browser: (state = {}) => state,
  context,
  currencyCode: currencyCodeReducer,
  experiments,
  features,
  installBanner,
  irclickid: (state = '') => state,
  lists,
  map: mapReducer,
  plans,
  requestCountryCode: (state = '' as CountryCode) => state,
  userConnectionLists,
  userConnections
});

export const buildStore = (preloadedState?: PreloadedState) =>
  configureStore({
    reducer,
    preloadedState,
    middleware: getDefaultMiddleware =>
      getDefaultMiddleware({ immutableCheck: false }).prepend(listenerMiddleware.middleware).concat(baseApi.middleware)
  });

export type AppStore = ReturnType<typeof buildStore>;
export type AppDispatch = AppStore['dispatch'];
export type RootState = ReturnType<AppStore['getState']> & { context?: Context };

// these are intended to use within the app only
export const useAppDispatch = (): AppDispatch => useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<RootState> = (selector: (state: RootState) => any) =>
  useSelector<RootState, any>(selector, shallowEqual);
